import { type TFunction } from 'i18next'
import * as R from 'remeda'

import {
  type CellInfo,
  compact,
  type DataDisplayType,
  exists,
  type FiveSegmentIndicator,
  fiveSegmentIndicatorLabelI18nKeyMap,
  fiveSegmentIndicators,
  fiveSegmentLabelI18nKeyMap,
  fiveSegments,
  getSortIconBySortValue,
  mapFiveSegment,
  NumFormat,
  type RowInfo,
  wrapFiveSegmentIndicatorLabelWithTranslate,
  wrapFiveSegmentLabelMapWithTranslate,
} from '~/utils'

import { calcAverage, calcMax } from './common'
import {
  type FiveSegmentColumnKey,
  fiveSegmentIndicatorHelpI18nMap,
} from './fiveSegments'

// Minimal interface that both analysis page and basic output brandsData satisfy
type BrandData = {
  brandId?: number | string
  brandName: string
  brandNumber: number
  counts: Record<string, number>
  ratios: Record<string, number>
}

type HiddenRowId = number | string

type BuildFiveSegmentsTableInfoParams = {
  addHighlightProps: (key: string) => Record<string, unknown>
  brandsData: BrandData[]
  dataDisplayType: DataDisplayType
  hiddenColumnKeys: FiveSegmentColumnKey[]
  hiddenRowIds: HiddenRowId[]
  indicatorPadClassName: string
  sortValue: { ascending: boolean; columnKey: string } | null
  t: TFunction
  tamRatio: number
}

export const buildFiveSegmentsTableInfo = ({
  addHighlightProps,
  brandsData,
  dataDisplayType,
  hiddenColumnKeys,
  hiddenRowIds,
  indicatorPadClassName,
  sortValue,
  t,
  tamRatio,
}: BuildFiveSegmentsTableInfoParams): RowInfo<FiveSegmentColumnKey>[] => {
  const visibleFiveSegments = R.difference(
    R.reverse(fiveSegments),
    hiddenColumnKeys,
  )
  const firstFiveSegment = visibleFiveSegments[0]

  const visibleIndicators = R.difference(
    fiveSegmentIndicators,
    hiddenColumnKeys,
  )
  const firstIndicator = visibleIndicators[0]

  const visibleBrandsData = brandsData.filter(
    (brand) => !exists(brand.brandId) || !hiddenRowIds.includes(brand.brandId),
  )
  const average = calcAverage(visibleBrandsData)
  const max = calcMax(visibleBrandsData)

  return compact([
    // Header 1
    {
      brandNumber: { colSpan: 2, isColumnHeader: true },
      ...(exists(firstFiveSegment)
        ? {
            [firstFiveSegment]: {
              align: 'center',
              colSpan: visibleFiveSegments.length,
              isColumnHeader: true,
              isHeader: true,
              text: t('5segs_distribution'),
            },
          }
        : {}),
      indicatorPad: {
        className: indicatorPadClassName,
        rowSpan: 4 + visibleBrandsData.length,
      },
      ...(exists(firstIndicator)
        ? {
            [firstIndicator]: {
              align: 'center',
              colSpan: visibleIndicators.length,
              isColumnHeader: true,
              isHeader: true,
              text: t('core_metrics'),
            },
          }
        : {}),
    },
    // Header 2
    {
      brandNumber: {
        align: 'center',
        colSpan: 2,
        isColumnHeader: true,
        isHeader: true,
        text: t('brand_name_3'),
      },
      ...R.mapToObj(
        [...fiveSegments, ...fiveSegmentIndicators],
        (indicator) => [
          indicator,
          {
            align: 'center',
            help: (() => {
              const helpKey =
                fiveSegmentIndicatorHelpI18nMap[
                  indicator as FiveSegmentIndicator
                ]

              return helpKey ? t(helpKey) : undefined
            })(),
            icon: getSortIconBySortValue(sortValue, indicator),
            isColumnHeader: true,
            isHeader: true,
            text: {
              ...wrapFiveSegmentLabelMapWithTranslate(
                fiveSegmentLabelI18nKeyMap,
                t,
              ),
              ...wrapFiveSegmentIndicatorLabelWithTranslate(
                fiveSegmentIndicatorLabelI18nKeyMap,
                t,
              ),
            }[indicator],
          } satisfies CellInfo,
        ],
      ),
    },
    // Brand rows
    ...R.pipe(
      visibleBrandsData,
      R.map((brand): RowInfo<FiveSegmentColumnKey> => ({
        brandNumber: { isHeader: true, text: brand.brandNumber },
        brandName: { isHeader: true, text: brand.brandName },
        ...mapFiveSegment((fiveSegment) => {
          const ratio = brand.ratios[fiveSegment]
          const count = brand.counts[fiveSegment]

          const text = NumFormat.forCount(
            count,
            ratio,
            tamRatio,
            dataDisplayType,
          )

          return {
            ratio,
            text,
            ...addHighlightProps(`${brand.brandName}-${fiveSegment}`),
          }
        }),
        ...R.mapToObj(fiveSegmentIndicators, (indicator) => {
          const ratio = brand.ratios[indicator]
          const text = NumFormat.forRatio(ratio)

          return [
            indicator,
            {
              ratio,
              text,
              ...addHighlightProps(`${brand.brandName}-${indicator}`),
            },
          ]
        }),
      })),
    ),
    // Average
    !hiddenRowIds.includes('average') && {
      brandNumber: { isHeader: true, colSpan: 2, text: t('average') },
      ...mapFiveSegment((fiveSegment): CellInfo => {
        if (!exists(average)) {
          return { text: '-' }
        }

        const ratio = average.ratio[fiveSegment]
        const count = average.counts[fiveSegment]

        return {
          text: NumFormat.forCount(count, ratio, tamRatio, dataDisplayType),
          ...addHighlightProps(`average-${fiveSegment}`),
        }
      }),
      ...R.mapToObj(fiveSegmentIndicators, (indicator) => [
        indicator,
        {
          text: exists(average)
            ? NumFormat.forRatio(average.ratio[indicator])
            : '-',
          ...addHighlightProps(`average-${indicator}`),
        },
      ]),
    },
    // Max
    !hiddenRowIds.includes('max') && {
      brandNumber: { isHeader: true, colSpan: 2, text: t('maximum_value') },
      ...mapFiveSegment((fiveSegment): CellInfo => {
        if (!exists(max)) {
          return { text: '-' }
        }

        const ratio = max.ratio[fiveSegment]
        const count = max.counts[fiveSegment]

        return {
          text: NumFormat.forCount(count, ratio, tamRatio, dataDisplayType),
          ...addHighlightProps(`max-${fiveSegment}`),
        }
      }),
      ...R.mapToObj(fiveSegmentIndicators, (indicator) => [
        indicator,
        {
          text: exists(max) ? NumFormat.forRatio(max.ratio[indicator]) : '-',
          ...addHighlightProps(`max-${indicator}`),
        },
      ]),
    },
  ])
}
